<script lang="ts" setup>
import { computed } from 'vue';
import { vElementVisibility } from '@vueuse/components';
import CardDefault from './Card.vue';
import CardLite from './CardLite.vue';
import type { SpikeItem, ICityItem, CardLogNormalParam } from './types';
import { getOpenFromByQuery } from '@/utils';
import { useRoute } from '@pet/base.utils';

interface MarketCardList {
    /**
     * 卡片样式
     */
    cardStyle?: 'default' | 'lite';
    /**
     * 自定义样式
     */
    customClsName?: string;
    /**
     * 资源key
     */
    resourceCode?: string;
    /**
     * 数据
     */
    itemData: SpikeItem[];
    /**
     * 商品Id列表(全国品)
     */
    commonItemIdStr?: string;
    /**
     * 商品Id信息(分城市商品)
     */
    cityItemConfig?: Array<ICityItem>;
    /**
     * 是否展示售罄标签
     */
    showSoldout?: boolean;
    /**
     * 按销量自动排序
     */
    autoSort?: boolean;
    /**
     * 筛选集Id
     */
    filterId?: string;
    cardLog?: (item: SpikeItem) => any;
    btnLog?: (item: SpikeItem) => any;
}

const props = defineProps<MarketCardList>();

const emit = defineEmits<{
    (e: 'click', params: SpikeItem): void;
    (e: 'btnClick', params: SpikeItem): void;
    (e: 'logEvent', params: CardLogNormalParam): void;
    (e: 'visible', params: SpikeItem, visible: boolean): void;
}>();

const onElementVisibility = (item: SpikeItem, state: boolean) => {
    emit('visible', item, state);
};

const itemList = computed(() => {
    return props.itemData.map((item: SpikeItem) => {
        const cardProps = {
            itemId: item.itemId,
            itemBanner: item.itemBanner,
            itemImage: item.itemImgUrl,
            itemName: item.itemTitle,
            tags: item.tags ?? [],
            soldStock: item.soldStock ?? 0,
            itemPrice: item.itemMarketPrice,
            discountPrice: item.discountPrice,
            disCountPercent: item.discount,
            itemOrderUrlKrn: item.itemOrderUrlKrn,
            brandName: item.brandName,
            priceGap: item.priceGap,
            itemDetailUrl: item.itemDetailUrl,
        };
        return cardProps;
    });
});

const handleBtnClick = (item: SpikeItem) => {
    emit('btnClick', item);
};

const handleItemClick = (item: SpikeItem) => {
    emit('click', item);
};

const handleLogEvent = (params: CardLogNormalParam) => {
    emit('logEvent', params);
};
</script>

<template>
    <div class="card-list">
        <div
            v-for="(item, index) in itemList"
            :key="`${index}_${item.itemName}}`"
            v-element-visibility="(state: boolean) => onElementVisibility({ ...item, index }, state)"
            class="card-item"
        >
            <slot v-if="$slots.customCard" name="customCard" :card-data="item" :index="index"></slot>
            <Component
                v-else
                :is="cardStyle === 'lite' ? CardLite : CardDefault"
                v-bind="item"
                class="card-list-item"
                @btnClick="(item) => handleBtnClick({ ...item, index })"
                @cardClick="(item) => handleItemClick({ ...item, index })"
                @logEvent="handleLogEvent"
                v-log="cardLog?.({ ...item, index }) ?? {}"
                :btn-log="btnLog?.({ ...item, index }) ?? {}"
            />
        </div>
    </div>
</template>

<style lang="stylus" scoped>
.card-list
  .card-item
    &:not(:first-child)
      margin-top 12px
</style>
